import { UrqlProvider as NextUrqlProvider, cacheExchange, fetchExchange, createClient, Exchange } from '@urql/next'
import { makeOperation } from '@urql/core'
import { useSession } from 'next-auth/react'
import { env } from 'environments'
import React from 'react'
import { authExchange } from '@urql/exchange-auth'
import { customFetch } from '@utils/custom-fetch'
import { createSpyingSSRExchange, ssrDataStore } from './utils'

export const UrqlProvider = ({ children }: { children: React.ReactNode | React.ReactNode[] }): React.ReactElement => {
  const { data: session } = useSession()

  const chainId = session?.chainId ?? env.chain.id
  const jwt = session?.jwt

  const [client, ssr] = React.useMemo(() => {
    const hasuraEndpoint = env.HASURA_GRAPHQL_ENDPOINT as string

    const ssr = createSpyingSSRExchange(chainId, {
      isClient: typeof window !== 'undefined'
    })
    const storedData = ssrDataStore.get(chainId)
    if (storedData) {
      ssr.restoreData(storedData)
    }

    const authEx = authExchange(async (_util) => {
      return {
        addAuthToOperation(operation) {
          if (!jwt || operation.context.forceNoAuth) {
            return operation
          }

          const fetchOptions =
            typeof operation.context.fetchOptions === 'function'
              ? operation.context.fetchOptions()
              : operation.context.fetchOptions || {}

          return makeOperation(operation.kind, operation, {
            ...operation.context,
            fetchOptions: {
              ...fetchOptions,
              headers: {
                ...fetchOptions.headers,
                'x-hasura-role': 'base_user',
                Authorization: `Bearer ${jwt}`
              }
            }
          })
        },
        async refreshAuth() {
          return
        },
        didAuthError(error) {
          return error.graphQLErrors.some(
            (e) => e.extensions?.code === 'FORBIDDEN' || e.extensions?.code === 'invalid-jwt'
          )
        }
      }
    })
    const client = createClient({
      url: hasuraEndpoint,
      exchanges: [cacheExchange, ssr, authEx as Exchange, fetchExchange],
      suspense: false,
      fetch: customFetch
    })

    return [client, ssr]
  }, [chainId, jwt])

  return (
    <NextUrqlProvider client={client} ssr={ssr}>
      {children}
    </NextUrqlProvider>
  )
}
